import React, { useEffect, useMemo, useState } from 'react';
import {
  Mail, Calendar, MousePointerClick, Eye, Users, ExternalLink,
  TrendingUp, TrendingDown, Minus, CalendarRange,
} from 'lucide-react';
import { fetchAdmin } from './_helpers';

// ---------- Helpers ----------
const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const startOfMonthIso = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const PRESETS = [
  { id: '7d', label: 'Last 7 days', start: () => daysAgoIso(6), end: todayIso },
  { id: '30d', label: 'Last 30 days', start: () => daysAgoIso(29), end: todayIso },
  { id: '90d', label: 'Last 90 days', start: () => daysAgoIso(89), end: todayIso },
  { id: 'mtd', label: 'This month', start: startOfMonthIso, end: todayIso },
  { id: 'custom', label: 'Custom', start: () => daysAgoIso(29), end: todayIso },
];

// Build a complete day-by-day series so the chart never gets gaps.
const padSeries = (rows, startIso, endIso) => {
  const map = new Map((rows || []).map((r) => [r.date, r.count]));
  const out = [];
  const cur = new Date(startIso + 'T00:00:00Z');
  const end = new Date(endIso + 'T00:00:00Z');
  while (cur <= end) {
    const key = cur.toISOString().slice(0, 10);
    out.push({ date: key, count: map.get(key) || 0 });
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
};

// ---------- Charts ----------
const BarChart = ({ data, height = 140, color = '#C9A86C', testId }) => {
  if (!data?.length) {
    return <div className="text-xs text-gray-400 py-8 text-center">No activity in this range.</div>;
  }
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="w-full">
      <div className="flex items-end gap-[2px]" style={{ height }} data-testid={testId}>
        {data.map((d, i) => {
          const h = (d.count / max) * (height - 16);
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center group relative">
              <div
                className="w-full rounded-sm transition-opacity hover:opacity-100 opacity-80"
                style={{ height: Math.max(h, d.count > 0 ? 2 : 0), background: color }}
              />
              <div className="absolute -top-7 hidden group-hover:block bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                {d.date}: <span className="font-semibold">{d.count}</span>
              </div>
            </div>
          );
        })}
      </div>
      {/* X-axis: show first / mid / last date labels */}
      <div className="flex justify-between text-[10px] text-gray-400 mt-2">
        <span>{data[0]?.date}</span>
        {data.length > 8 && <span>{data[Math.floor(data.length / 2)]?.date}</span>}
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
};

const Trend = ({ pct }) => {
  if (pct === null || pct === undefined) return <span className="text-xs text-gray-400 inline-flex items-center gap-1"><Minus size={12} /> n/a</span>;
  if (pct > 0) return <span className="text-xs text-green-600 inline-flex items-center gap-1"><TrendingUp size={12} /> +{pct}%</span>;
  if (pct < 0) return <span className="text-xs text-red-600 inline-flex items-center gap-1"><TrendingDown size={12} /> {pct}%</span>;
  return <span className="text-xs text-gray-500 inline-flex items-center gap-1"><Minus size={12} /> 0%</span>;
};

const StatCard = ({ Icon, label, rangeCount, total, pct, rangeDays, testId }) => (
  <div className="bg-white rounded-xl p-5 shadow-md" data-testid={testId}>
    <div className="flex items-start justify-between mb-3">
      <Icon className="text-[#C9A86C]" size={28} />
      <Trend pct={pct} />
    </div>
    <div className="text-3xl font-bold leading-none">{(rangeCount ?? 0).toLocaleString()}</div>
    <div className="text-xs text-gray-500 mt-1">selected range ({rangeDays}d)</div>
    <div className="text-sm text-gray-700 mt-2">
      <span className="font-medium">{(total ?? 0).toLocaleString()}</span> {label}
      <div className="text-xs text-gray-400">all-time</div>
    </div>
  </div>
);

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  qualified: 'bg-purple-100 text-purple-700',
  won: 'bg-green-100 text-green-700',
  lost: 'bg-gray-100 text-gray-600',
};

// ---------- Main ----------
const ReportsManager = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState('30d');
  const [start, setStart] = useState(daysAgoIso(29));
  const [end, setEnd] = useState(todayIso());

  const load = async (s, e) => {
    setLoading(true);
    const q = `analytics/summary?start=${s}&end=${e}`;
    const d = await fetchAdmin(q);
    setData(d);
    setLoading(false);
  };

  useEffect(() => { load(start, end); /* eslint-disable-next-line */ }, []);

  const applyPreset = (id) => {
    const p = PRESETS.find((x) => x.id === id);
    setPreset(id);
    if (id !== 'custom') {
      const s = p.start();
      const e = p.end();
      setStart(s);
      setEnd(e);
      load(s, e);
    }
  };

  const applyCustom = () => {
    setPreset('custom');
    load(start, end);
  };

  // Pad chart series so empty days still show
  const pvSeries = useMemo(() => padSeries(data?.page_views?.daily_in_range, start, end), [data, start, end]);
  const clickSeries = useMemo(() => padSeries(data?.reserve_clicks?.daily_in_range, start, end), [data, start, end]);
  const visitorSeries = useMemo(() => padSeries(data?.visitors?.daily_in_range, start, end), [data, start, end]);

  if (loading && !data) return <div className="text-gray-500">Loading…</div>;
  if (!data) return <div className="text-gray-500">No data.</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Reports</h2>
          <p className="text-sm text-gray-500 mt-1">
            First-party metrics collected from your own backend. Updated in real time.
          </p>
        </div>
        <button
          onClick={() => load(start, end)}
          className="text-sm bg-white border rounded-lg px-3 py-2 hover:bg-gray-50"
          data-testid="reports-refresh-btn"
        >
          Refresh
        </button>
      </div>

      {/* Range picker */}
      <div className="bg-white rounded-xl shadow-md p-4" data-testid="reports-range-picker">
        <div className="flex flex-wrap items-center gap-2">
          <CalendarRange size={18} className="text-[#C9A86C]" />
          <span className="text-sm font-medium text-gray-700 mr-2">Date range:</span>
          {PRESETS.filter((p) => p.id !== 'custom').map((p) => (
            <button
              key={p.id}
              onClick={() => applyPreset(p.id)}
              data-testid={`range-preset-${p.id}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                preset === p.id
                  ? 'bg-[#C9A86C] text-white border-[#C9A86C]'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-[#C9A86C] hover:text-[#C9A86C]'
              }`}
            >
              {p.label}
            </button>
          ))}
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-xs">
            <input
              type="date"
              value={start}
              max={end}
              onChange={(e) => { setStart(e.target.value); setPreset('custom'); }}
              className="border rounded px-2 py-1"
              data-testid="range-start-input"
            />
            <span className="text-gray-400">→</span>
            <input
              type="date"
              value={end}
              min={start}
              max={todayIso()}
              onChange={(e) => { setEnd(e.target.value); setPreset('custom'); }}
              className="border rounded px-2 py-1"
              data-testid="range-end-input"
            />
            <button
              onClick={applyCustom}
              className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs hover:bg-gray-800"
              data-testid="range-apply-btn"
            >
              Apply
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Showing data from <strong>{data.range?.start}</strong> to <strong>{data.range?.end}</strong> · % change vs previous {data.range?.days}-day window.
        </p>
      </div>

      {/* Top-line stats — driven by selected range */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="reports-stat-cards">
        <StatCard
          Icon={Users} label="unique visitors"
          rangeCount={data.visitors?.range_count} total={data.visitors?.all_time}
          pct={data.visitors?.range_pct} rangeDays={data.range?.days}
          testId="card-visitors"
        />
        <StatCard
          Icon={Eye} label="page views"
          rangeCount={data.page_views.range_count} total={data.page_views.total}
          pct={data.page_views.range_pct} rangeDays={data.range?.days}
          testId="card-page-views"
        />
        <StatCard
          Icon={MousePointerClick} label="reserve clicks"
          rangeCount={data.reserve_clicks.range_count} total={data.reserve_clicks.total}
          pct={data.reserve_clicks.range_pct} rangeDays={data.range?.days}
          testId="card-reserve-clicks"
        />
        <StatCard
          Icon={Calendar} label="event inquiries"
          rangeCount={data.inquiries.range_count} total={data.inquiries.total}
          pct={data.inquiries.range_pct} rangeDays={data.range?.days}
          testId="card-inquiries"
        />
        <StatCard
          Icon={Mail} label="newsletter subscribers"
          rangeCount={data.subscribers.range_count} total={data.subscribers.total}
          pct={data.subscribers.range_pct} rangeDays={data.range?.days}
          testId="card-subscribers"
        />
      </div>

      {/* Daily charts: visitors + page views + reserve clicks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Daily unique visitors</h3>
            <span className="text-xs text-gray-500">{data.visitors?.range_count?.toLocaleString()} total</span>
          </div>
          <BarChart data={visitorSeries} color="#6B4F30" testId="visitors-bar-chart" />
          <div className="mt-3 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
            <span><strong className="text-gray-700">{data.visitors?.sessions_in_range?.toLocaleString() ?? 0}</strong> sessions</span>
            <span><strong className="text-gray-700">{data.visitors?.views_per_visitor ?? 0}</strong> views / visitor</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Daily page views</h3>
            <span className="text-xs text-gray-500">{data.page_views.range_count?.toLocaleString()} total</span>
          </div>
          <BarChart data={pvSeries} color="#C9A86C" testId="pv-bar-chart" />
        </div>

        <div className="bg-white rounded-xl p-6 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Daily reserve clicks</h3>
            <span className="text-xs text-gray-500">{data.reserve_clicks.range_count?.toLocaleString()} total</span>
          </div>
          <BarChart data={clickSeries} color="#0f0f0e" testId="click-bar-chart" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top pages */}
        <div className="bg-white rounded-xl p-6 shadow-md lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">Top pages in this range</h3>
          {data.page_views.top_pages.length === 0 ? (
            <p className="text-sm text-gray-400">No page-view data in this range.</p>
          ) : (
            <ul className="divide-y" data-testid="top-pages-list">
              {data.page_views.top_pages.map((p) => (
                <li key={p.path} className="py-2 flex items-center justify-between text-sm">
                  <code className="text-gray-700">{p.path}</code>
                  <span className="text-gray-500 font-medium">{p.count.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Reserve clicks by location (within range) */}
        <div className="bg-white rounded-xl p-6 shadow-md">
          <h3 className="text-lg font-semibold mb-4">Reserve clicks by location</h3>
          {data.reserve_clicks.by_location.length === 0 ? (
            <p className="text-sm text-gray-400">No reservation clicks in this range.</p>
          ) : (
            <ul className="space-y-2" data-testid="reserve-by-location">
              {data.reserve_clicks.by_location.map((r) => {
                const max = data.reserve_clicks.by_location[0].count;
                const width = `${Math.round((r.count / max) * 100)}%`;
                return (
                  <li key={r.location}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="text-gray-700">{r.location}</span>
                      <span className="text-gray-500 font-medium">{r.count}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded">
                      <div className="h-1.5 rounded bg-[#C9A86C]" style={{ width }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inquiries — status + recent (all-time) */}
        <div className="bg-white rounded-xl p-6 shadow-md">
          <h3 className="text-lg font-semibold mb-4">Event Inquiries</h3>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="inquiries-status">
            {Object.entries(data.inquiries.status_breakdown).map(([k, v]) => (
              <span key={k} className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[k] || 'bg-gray-100 text-gray-700'}`}>
                {k}: {v}
              </span>
            ))}
          </div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Recent leads</h4>
          {data.inquiries.recent.length === 0 ? (
            <p className="text-sm text-gray-400">No inquiries yet.</p>
          ) : (
            <ul className="divide-y">
              {data.inquiries.recent.map((r, i) => (
                <li key={i} className="py-2.5 text-sm" data-testid={`recent-lead-${i}`}>
                  <div className="flex justify-between">
                    <span className="font-medium">{r.name}</span>
                    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {r.email} · {r.event_type || '—'} · {r.guest_count ? `${r.guest_count} guests` : '—'}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* External dashboards quick links */}
        <div className="bg-white rounded-xl p-6 shadow-md">
          <h3 className="text-lg font-semibold mb-1">Where else to look</h3>
          <p className="text-sm text-gray-500 mb-4">
            Detailed traffic sources, ad performance, audiences, and session replays live in the external tools.
          </p>
          <div className="space-y-2.5">
            {[
              { name: 'Google Analytics 4', desc: 'Sessions, sources, funnels, demographics', url: 'https://analytics.google.com/' },
              { name: 'Meta Events Manager', desc: 'Pixel + CAPI events, match quality, audiences', url: 'https://business.facebook.com/events_manager' },
              { name: 'Google Ads', desc: 'Cost-per-conversion, CTR, ad performance', url: 'https://ads.google.com/' },
              { name: 'TikTok Ads Manager', desc: 'TikTok pixel + ad performance', url: 'https://ads.tiktok.com/i18n/events_manager/' },
              { name: 'PostHog', desc: 'Session replays, heatmaps, real-time events', url: 'https://app.posthog.com/' },
            ].map((it) => (
              <a
                key={it.name}
                href={it.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div>
                  <div className="text-sm font-medium">{it.name}</div>
                  <div className="text-xs text-gray-500">{it.desc}</div>
                </div>
                <ExternalLink size={16} className="text-gray-400" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsManager;
